import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteFirmUser } from "@/hooks/useDeleteFirmUser";
import { Plus, Trash2, X } from "lucide-react";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { useAPI } from "../../hooks/useAPI";
import { getErrorMessage } from "../../utils";
import { filterReqRolesFromAll, getDefaultAssignableRoleId } from "../../utils/roleUtils";

const EditableCell = ({ value, onSave, type = "text" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        type={type}
        className="w-full rounded-md border px-2 py-1"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyPress={(e) => e.key === "Enter" && handleSave()}
        autoFocus
      />
    );
  }

  return (
    <div
      className="cursor-pointer rounded px-2 py-1 hover:bg-surface-subtle"
      onClick={() => setIsEditing(true)}
    >
      {value}
    </div>
  );
};

EditableCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSave: PropTypes.func.isRequired,
  type: PropTypes.string,
};

const InviteUserModal = ({ isOpen, onClose, roles, onInvite }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role_id: getDefaultAssignableRoleId(roles, ["analyst", "admin"]),
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (roles.length > 0) {
      setFormData((prev) => ({
        ...prev,
        role_id: getDefaultAssignableRoleId(roles, ["analyst", "admin"]),
      }));
    }
  }, [roles]);

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        email: "",
        name: "",
        role_id: getDefaultAssignableRoleId(roles, ["analyst", "admin"]),
      });
      setIsSubmitting(false);
    }
  }, [isOpen, roles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onInvite({
        ...formData,
        role_id: parseInt(formData.role_id, 10),
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(next) => {
        if (isSubmitting) return;
        if (!next) onClose();
      }}
    >
      <DialogContent
        size="sm"
        hideCloseButton
        onInteractOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0 text-left">
          <DialogTitle className="text-xl font-semibold">Invite New User</DialogTitle>
          <DialogClose className="rounded p-1 hover:bg-surface-sunken" disabled={isSubmitting}>
            <X className="h-5 w-5" />
          </DialogClose>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-muted-foreground">Name</label>
              <input
                type="text"
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground">Email</label>
              <input
                type="email"
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-muted-foreground">Role</label>
              <select
                required
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-md border border-input px-3 py-2 disabled:cursor-not-allowed disabled:bg-surface-sunken"
                value={formData.role_id}
                onChange={(e) => setFormData({ ...formData, role_id: e.target.value })}
              >
                {filterReqRolesFromAll(roles, ["analyst", "admin"]).map((role) => (
                  <option key={role.role_id} value={role.role_id}>
                    {role.role_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-md border border-input px-4 py-2 text-muted-foreground hover:bg-surface-subtle disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSubmitting ? "Sending..." : "Send Invitation"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

InviteUserModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roles: PropTypes.array.isRequired,
  onInvite: PropTypes.func.isRequired,
};

const UserManagement = () => {
  const api = useAPI();
  const { deleteFirmUser } = useDeleteFirmUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roles, setRoles] = useState([]);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users/firm");
      setUsers(data);
    } catch (error) {
      if (error.message === "Session expired") {
        return; // API service will handle redirect
      }
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (error) {
      console.error("Error fetching roles:", error);
    }
  };

  const handleUpdate = async (userId, field, value) => {
    try {
      const updateData = {
        user_id: userId,
        [field]: field === "role_id" ? parseInt(value, 10) : value,
      };

      await api.put(`/users/${userId}`, updateData);
      setUsers(users.map((user) => (user.user_id === userId ? { ...user, [field]: value } : user)));
    } catch (error) {
      console.error("Error updating user:", error);
      alert("Failed to update user. Please try again.");
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      await deleteFirmUser(userId);
      setUsers(users.filter((user) => user.user_id !== userId));
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(getErrorMessage(error));
    }
  };

  const handleInviteUser = async (userData) => {
    await api.post("/users/invite", userData);
    setIsInviteModalOpen(false);
    toast.success("Invitation sent successfully");
    await fetchUsers();
  };

  if (loading) {
    return <div className="flex h-64 items-center justify-center">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="rounded-lg bg-card shadow">
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-semibold text-foreground">User Management</h2>
          <button
            onClick={() => setIsInviteModalOpen(true)}
            className="flex items-center space-x-2 rounded-md bg-primary px-4 py-2 text-white hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" />
            <span>New User</span>
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface-subtle">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Email
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Role
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-micro text-muted-foreground"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {users.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground">
                    No users found. Click &quot;New User&quot; to invite a user to your firm.
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.user_id}>
                    <td className="whitespace-nowrap px-6 py-4">
                      <EditableCell
                        value={user.email}
                        onSave={(value) => handleUpdate(user.user_id, "email", value)}
                        type="email"
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <EditableCell
                        value={user.name}
                        onSave={(value) => handleUpdate(user.user_id, "name", value)}
                      />
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <select
                        className="min-w-[150px] rounded-md border px-2 py-1"
                        value={user.role_id}
                        onChange={(e) => handleUpdate(user.user_id, "role_id", e.target.value)}
                      >
                        {filterReqRolesFromAll(roles, ["analyst", "admin"]).map((role) => (
                          <option key={role.role_id} value={role.role_id}>
                            {role.role_name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="whitespace-nowrap px-6 py-4">
                      <button
                        onClick={() => handleDelete(user.user_id)}
                        className="text-destructive-text hover:text-destructive"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        roles={roles}
        onInvite={handleInviteUser}
      />
    </div>
  );
};

export default UserManagement;
